/**
 * Pure helpers for persisting the offline PostSection / RealTimeResponse queue in localStorage.
 * Drain/process stays in TestSections; these helpers are unit-tested in isolation.
 */

export const OFFLINE_QUEUE_STORAGE_PREFIX = 'rt.offlineQueue.v1';

export function getOfflineQueueStorageKey(testId: number | string | null | undefined): string {
  const resolvedTestId = Number(testId || 0);
  return `${OFFLINE_QUEUE_STORAGE_PREFIX}:${resolvedTestId}`;
}

export function readOfflineQueue(storage: Storage, testId: number | string | null | undefined): unknown[] {
  try {
    const raw = storage.getItem(getOfflineQueueStorageKey(testId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function writeOfflineQueue(
  storage: Storage,
  testId: number | string | null | undefined,
  queue: unknown[] | null | undefined
): void {
  try {
    const key = getOfflineQueueStorageKey(testId);
    if (!Array.isArray(queue) || queue.length === 0) {
      storage.removeItem(key);
      return;
    }
    storage.setItem(key, JSON.stringify(queue));
  } catch {
    // Ignore storage errors; callers keep an in-memory queue for the session.
  }
}

export function clearOfflineQueue(storage: Storage, testId: number | string | null | undefined): void {
  writeOfflineQueue(storage, testId, []);
}

export function enqueueOfflineItem(
  queue: unknown[] | null | undefined,
  item: unknown
): unknown[] {
  const safeQueue = Array.isArray(queue) ? [...queue] : [];
  safeQueue.push(item);
  return safeQueue;
}

/**
 * After a successful send, drop only the head item from the *live* queue.
 * Never rebuild remaining items from a pre-drain snapshot — answers enqueued
 * while reconnect flush is in flight must be preserved.
 */
export function dequeueOfflineQueueHead(
  queue: unknown[] | null | undefined
): unknown[] {
  const safeQueue = Array.isArray(queue) ? queue : [];
  return safeQueue.length > 0 ? safeQueue.slice(1) : [];
}

/** Done section must wait until the offline queue is empty. */
export function isDoneBlockedByOfflineQueue(pendingCount: number): boolean {
  return Number(pendingCount || 0) > 0;
}
